import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { hospitalAdminService, adminService } from '../../api/services';

export const fetchHospitalDashboard = createAsyncThunk(
  'hospitals/fetchDashboard',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await hospitalAdminService.getDashboard();
      return data;
    } catch (error) {
      return rejectWithValue('Failed to fetch dashboard data.');
    }
  }
);

export const fetchHospitalDoctors = createAsyncThunk(
  'hospitals/fetchDoctors',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await hospitalAdminService.getDoctors(params);
      return data;
    } catch (error) {
      return rejectWithValue('Failed to fetch doctors.');
    }
  }
);

export const fetchHospitalBranches = createAsyncThunk(
  'hospitals/fetchBranches',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await hospitalAdminService.getBranches(params);
      return data;
    } catch (error) {
      return rejectWithValue('Failed to fetch branches.');
    }
  }
);

export const fetchHospitalAppointments = createAsyncThunk(
  'hospitals/fetchAppointments',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await hospitalAdminService.getAppointments(params);
      return data;
    } catch (error) {
      return rejectWithValue('Failed to fetch appointments.');
    }
  }
);

export const fetchHospitalRevenue = createAsyncThunk(
  'hospitals/fetchRevenue',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await hospitalAdminService.getRevenue(params);
      return data;
    } catch (error) {
      return rejectWithValue('Failed to fetch revenue data.');
    }
  }
);

export const fetchAdminDashboard = createAsyncThunk(
  'hospitals/fetchAdminDashboard',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await adminService.getDashboard();
      return data;
    } catch (error) {
      return rejectWithValue('Failed to fetch admin dashboard.');
    }
  }
);

const initialState = {
  dashboard: null,
  doctors: [],
  branches: [],
  appointments: [],
  revenue: null,
  loading: false,
  error: null,
};

const hospitalSlice = createSlice({
  name: 'hospitals',
  initialState,
  reducers: {
    clearHospitalError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchHospitalDashboard.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchHospitalDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboard = action.payload;
      })
      .addCase(fetchHospitalDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchHospitalDoctors.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchHospitalDoctors.fulfilled, (state, action) => {
        state.loading = false;
        state.doctors = action.payload.results || action.payload;
      })
      .addCase(fetchHospitalDoctors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchHospitalBranches.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchHospitalBranches.fulfilled, (state, action) => {
        state.loading = false;
        state.branches = action.payload.results || action.payload;
      })
      .addCase(fetchHospitalBranches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchHospitalAppointments.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchHospitalAppointments.fulfilled, (state, action) => {
        state.loading = false;
        state.appointments = action.payload.results || action.payload;
      })
      .addCase(fetchHospitalAppointments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchHospitalRevenue.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchHospitalRevenue.fulfilled, (state, action) => {
        state.loading = false;
        state.revenue = action.payload;
      })
      .addCase(fetchHospitalRevenue.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAdminDashboard.fulfilled, (state, action) => {
        state.dashboard = action.payload;
        state.loading = false;
      });
  },
});

export const { clearHospitalError } = hospitalSlice.actions;
export default hospitalSlice.reducer;
